import React, { useEffect } from 'react';
import {
  Edit,
  SimpleForm,
  ArrayInput,
  SimpleFormIterator,
  FileInput,
  ImageField,
  useUpdate,
  useNotify,
  useRedirect,
  Toolbar,
  SaveButton,
  required
} from 'react-admin';
import { RichTextInput } from 'ra-input-rich-text';
import { blocksToHtml, htmlToBlocks } from '../../../helpers/htmlUtils';
import { uploadImage } from '../../../config/services/strapi/strapi';

const getPreviewUrl = (file: any) => {
  const raw =
    file?.src ||
    file?.url ||
    file?.data?.attributes?.url ||
    file?.attributes?.url ||
    file?.formats?.thumbnail?.url ||
    file?.formats?.small?.url ||
    file?.formats?.medium?.url ||
    file?.formats?.large?.url ||
    '';

  if (!raw) return '';
  if (raw.startsWith('http') || raw.startsWith('blob:') || raw.startsWith('data:')) return raw;

  const base = process.env.REACT_APP_STRAPI_ENDPOINT || 'http://localhost:1337';
  return `${base}${raw}`;
};

const toInputFile = (file: any) => {
  if (!file) return null;

  const src = getPreviewUrl(file);
  const title = file?.title || file?.name || file?.alternativeText || 'imagen';
  if (!src) return null;

  return {
    ...file,
    src,
    title,
  };
};

const normalizeImagenForInput = (value: any) => {
  if (!value) return value;
  if (Array.isArray(value)) {
    return value.map((item: any) => toInputFile(item) || item);
  }

  return toInputFile(value) || value;
};

const getMediaId = (value: any) => {
  if (!value) return null;
  if (typeof value?.id === 'number') return value.id;
  if (typeof value?.data?.id === 'number') return value.data.id;
  if (typeof value?.attributes?.id === 'number') return value.attributes.id;
  return null;
};

const getJwtOrNotify = (notify: any, message: string) => {
  const jwt = localStorage.getItem('strapi_jwt');
  if (!jwt) {
    notify(message, { type: 'warning' });
    return null;
  }

  return jwt;
};

const uploadHeroItems = async (heroItem: any, notify: any) => {
  if (!Array.isArray(heroItem) || !heroItem.length) return [];

  const jwt = getJwtOrNotify(notify, 'No autorizado para subir imágenes');
  if (!jwt) return [];

  const uploaded: any[] = [];
  for (const item of heroItem) {
    const entry: Record<string, any> = {
      contenido: item?.contenido,
    };

    const file = item?.imagen;
    if (file?.rawFile) {
      const up = await uploadImage(file.rawFile, jwt);
      if (!up?.id) continue;
      entry.imagen = { id: up.id };
    } else {
      const existingId = getMediaId(file);
      if (existingId) {
        entry.imagen = { id: existingId };
      }
    }

    if (item?.id) {
      entry.id = item.id;
    }

    uploaded.push(entry);
  }

  return uploaded;
};

function ArchivoPatrimonialEdit(props: any) {
  const [update] = useUpdate();
  const redirect = useRedirect();
  const notify = useNotify();
  const resourceName = 'fg-archivo-patrimonials';

  useEffect(() => {
    document.title = 'Editar Archivo Patrimonial';
  }, []);

  const handleSubmit = async (data: any) => {
    try {
      const payload: Record<string, any> = {
        tituloLeft: data.tituloLeft,
        descripcion: data.descripcion,
        detalle: data.detalle,
        textoColor: data.textoColor,
      };

      const heroItem = await uploadHeroItems(data.heroItem, notify);
      if (Array.isArray(data.heroItem) && data.heroItem.length && !heroItem.length) return;
      if (heroItem.length) payload.heroItem = heroItem;

      update(resourceName, { id: data.id, data: payload }, {
        onSuccess: () => {
          notify('Archivo Patrimonial actualizado', { type: 'success' });
          redirect('list', resourceName);
        },
        onError: (err: any) => {
          notify(`Error: ${err?.message || err}`, { type: 'error' });
        }
      });
    } catch (e: any) {
      console.error(e);
      notify(`Error al actualizar: ${e?.message || 'desconocido'}`, { type: 'error' });
    }
  };

  return (
    <Edit {...props}>
      <SimpleForm onSubmit={handleSubmit} toolbar={<Toolbar><SaveButton alwaysEnable /></Toolbar>}>

        <RichTextInput
          source="descripcion"
          label="Descripción Izquierda"
          format={blocksToHtml}
          parse={htmlToBlocks}
          validate={[required()]}
          fullWidth
        />

        <RichTextInput
          source="detalle"
          label="Detalle Columna Derecha"
          format={blocksToHtml}
          parse={htmlToBlocks}
          validate={[required()]}
          fullWidth
        />

        <ArrayInput source="heroItem" label="Hero Items">
          <SimpleFormIterator inline>
            <FileInput source="imagen" label="Imagen" format={normalizeImagenForInput}>
              <ImageField source="src" title="title" />
            </FileInput>
            <RichTextInput
              source="contenido"
              label="Descripción de la imagen"
              format={blocksToHtml}
              parse={htmlToBlocks}
              fullWidth
            />
          </SimpleFormIterator>
        </ArrayInput>
      </SimpleForm>
    </Edit>
  );
}

export default ArchivoPatrimonialEdit;
